import type { Offers } from 'queries/graphql';

// Note: This utility is designed to work with GraphQL data from the storefront
// For direct database queries, use the checkout project utilities
/**
 * Utility functions for working with selling plans and generating Skio URLs
 */

export interface SkioUrlData {
  storeDomain: string;
  variantId: string;
  sellingPlanId: string;
}

export interface SellingPlanWithSkioData {
  id: number;
  shopifyPlanId: string;
  name: string;
  category: string;
  productId: string;
  skioUrl: string | null;
}

/**
 * Generate a Skio subscription URL from the provided data
 * Format: https://{store_domain}/cart/clear?return_to=/cart/add?items[0][id]={variant_id}&items[0][selling_plan]={selling_plan_id}&items[0][quantity]=1&return_to=/checkout
 */
export function generateSkioUrl(data: SkioUrlData): string {
  const { storeDomain, variantId, sellingPlanId } = data;

  if (!storeDomain || !variantId || !sellingPlanId) {
    throw new Error(
      'Missing required data for Skio URL generation: storeDomain, variantId, or sellingPlanId'
    );
  }

  // Clean the store domain (remove protocol and trailing slashes)
  const cleanDomain = storeDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');

  // Build the inner cart URL with proper encoding
  const cartUrl = `/cart/add?items[0][id]=${encodeURIComponent(
    variantId
  )}&items[0][selling_plan]=${encodeURIComponent(
    sellingPlanId
  )}&items[0][quantity]=1&return_to=/checkout`;

  // Build the final URL with proper encoding
  return `https://${cleanDomain}/cart/clear?return_to=${encodeURIComponent(cartUrl)}`;
}

/**
 * Generate Skio URLs for all selling plans associated with a specific offer
 * This function expects the offer data to be passed in with all necessary relationships
 * Use this with GraphQL queries from the storefront
 */
export function generateSkioUrlsForOffer(offerData: Offers): SellingPlanWithSkioData[] {
  const { store, offerItems } = offerData;
  const offer = offerItems?.[0];
  const productVariant = offer?.productVariant;
  const productVariantId = productVariant?.productVariantId;
  const sellingPlans = productVariant?.sellingPlans;

  if (!store?.domain || !productVariantId || !sellingPlans) {
    console.error(`Missing required data for offer ${offerData.id}:`, {
      storeDomain: store?.domain,
      productVariantId,
      sellingPlansCount: sellingPlans?.length,
    });
    return [];
  }

  return sellingPlans.map(sellingPlan => {
    let skioUrl: string | null = null;

    try {
      skioUrl = generateSkioUrl({
        storeDomain: store.domain,
        variantId: productVariantId,
        sellingPlanId: sellingPlan.shopifyPlanId,
      });
    } catch (error) {
      console.error('Error generating Skio URL for selling plan:', sellingPlan, error);
      skioUrl = null;
    }

    return {
      id: sellingPlan.id,
      shopifyPlanId: sellingPlan.shopifyPlanId,
      name: sellingPlan.name,
      category: sellingPlan.category,
      productId: sellingPlan.productVariantId,
      skioUrl,
    };
  });
}

/**
 * Generate a single Skio URL for a specific selling plan and offer combination
 */
export function generateSkioUrlForSellingPlan(
  storeDomain: string,
  variantId: string,
  sellingPlanId: string
): string | null {
  try {
    return generateSkioUrl({
      storeDomain,
      variantId,
      sellingPlanId,
    });
  } catch (error) {
    console.error('Error generating Skio URL:', error);
    return null;
  }
}

/**
 * Validate if a Skio URL can be generated with the provided data
 */
export function canGenerateSkioUrl(data: Partial<SkioUrlData>): boolean {
  return !!(data.storeDomain && data.variantId && data.sellingPlanId);
}

/**
 * Extract selling plan ID from Shopify GID format
 * Converts: gid://shopify/SellingPlan/123456789 -> 123456789
 */
export function extractSellingPlanId(gid: string): string {
  return gid.replace('gid://shopify/SellingPlan/', '');
}

/**
 * Extract variant ID from Shopify GID format
 * Converts: gid://shopify/ProductVariant/123456789 -> 123456789
 */
export function extractVariantId(gid: string): string {
  return gid.replace('gid://shopify/ProductVariant/', '');
}
